import classNames from 'classnames';
import React, {useRef} from 'react';
import _ from 'underscore';

import getTestProps from 'test/lib/cypress/getTestProps';
import Label from 'ui-library/bs/Label';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

/**
 * Component for selecting the [source, destination] column when inserting samples into a
 * structured table. It is a controlled component; the caller must keep track of the current
 * selection by using the `onChange` callback.
 */

export interface ColumnSelectorProps {
  label: string;
  columns: Array<{
    name: string;
    icon?: BenchlingIconName;
  }>;
  onChange: (columnIndex: number) => void;
  selectedColumnIndex: number;
  disabled?: boolean;
  disableWhenOnlyOneColumn?: boolean;
}

export default function ColumnSelector(props: ColumnSelectorProps): JSX.Element {
  const controlId = useRef(_.uniqueId('colselector_'));
  const {columns, selectedColumnIndex} = props;
  const displayValue = columns[selectedColumnIndex]?.name ?? '';
  const disableWhenOnlyOneColumn = props.disableWhenOnlyOneColumn ?? true;
  return (
    <span {...getTestProps({component: 'ColumnSelector', element: 'root'})}>
      <Label
        htmlFor={controlId.current}
        className={classNames('u-small-spacing-top', props.disabled && 'faded')}
      >
        {props.label}
      </Label>
      <SelectDropdown
        id={controlId.current}
        disabled={Boolean(props.disabled) || (disableWhenOnlyOneColumn && columns.length === 1)}
        menu={
          <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={displayValue}>
            {columns.map((columnInfo, index) => (
              <Option
                key={columnInfo.name + index}
                icon={columnInfo.icon}
                value={columnInfo.name}
                onSelect={() => {
                  props.onChange(index);
                }}
              >
                {columnInfo.name}
              </Option>
            ))}
          </Menu>
        }
        displayName={displayValue}
      />
    </span>
  );
}
